import Phaser from 'phaser';
import { SceneKeys } from '../game/Constants';
import { VisualTheme } from '../ui/VisualTheme';
import { PlayerPreferences } from './PlayerPreferences';
import { TutorialManager } from './TutorialManager';

const OVERLAY_KEYS = new Set<string>([SceneKeys.Settings, SceneKeys.Tutorial]);
const NON_PAUSABLE_KEYS = new Set<string>([SceneKeys.Title, SceneKeys.MainMenu, SceneKeys.Pause, SceneKeys.Boot, SceneKeys.Preload]);
const HINTS: Record<string, string> = {
  [SceneKeys.CampaignMap]: 'Select a province for details. Right-click a destination to march the selected army. Press M to cycle map modes.',
  [SceneKeys.Province]: 'Food, workforce and transport constrain production. Inspect the monthly ledger before expanding a building chain.',
  [SceneKeys.Diplomacy]: 'Opinion is only one signal: trust, threat, favors, treaties and strategic interest explain diplomatic behavior.',
  [SceneKeys.Court]: 'Succession clarity and estate approval can matter more than treasury during a disputed inheritance.',
  [SceneKeys.Trade]: 'Shortages raise local prices. Ports, roads and diplomatic access determine how far a surplus can travel.',
  [SceneKeys.Tech]: 'Research focus accelerates one branch; institutions and prerequisites still gate advanced technologies.',
  [SceneKeys.Espionage]: 'Success and exposure are separate risks. Build infiltration and reduce heat before committing to major operations.',
  [SceneKeys.Naval]: 'Weather, transport capacity and sea control should be checked before ordering an amphibious landing.',
  [SceneKeys.Battle]: 'Use terrain, facing and reserves. Spears punish mounted attacks; exposed ranged units need protection.',
};

export class GlobalUXManager {
  static readonly instance = new GlobalUXManager();
  private installed = false;
  private tutorialLaunchQueued = false;
  private lastHintScene?: string;
  private gamepadLatch = false;

  install(game: Phaser.Game) {
    if (this.installed) return;
    this.installed = true;
    const canvas = game.canvas;
    canvas.setAttribute('role', 'application');
    canvas.setAttribute('aria-label', 'Conquest of Java grand strategy game. Press F1 for the guided tutorial and F10 for settings.');
    canvas.tabIndex = 0;

    window.addEventListener('keydown', (event) => {
      if (event.key === 'F1') {
        event.preventDefault();
        this.openOverlay(game, SceneKeys.Tutorial);
      }
      if (event.key === 'F10') {
        event.preventDefault();
        this.openOverlay(game, SceneKeys.Settings);
      }
      if (event.key === 'Escape') this.handleEscape(game);
    });

    game.events.on(Phaser.Core.Events.STEP, () => {
      this.autoLaunchTutorial(game);
      this.showContextHint(game);
      this.pollGlobalGamepad(game);
    });
  }

  private activeBaseScene(game: Phaser.Game) {
    return game.scene.getScenes(true).find((scene) => !OVERLAY_KEYS.has(scene.scene.key));
  }

  private openOverlay(game: Phaser.Game, key: typeof SceneKeys.Settings | typeof SceneKeys.Tutorial) {
    const active = this.activeBaseScene(game);
    if (!active || game.scene.isActive(key)) return;
    game.scene.pause(active.scene.key);
    game.scene.run(key, { returnScene: active.scene.key });
  }

  private handleEscape(game: Phaser.Game) {
    if (game.scene.isActive(SceneKeys.Settings) || game.scene.isActive(SceneKeys.Tutorial)) return;
    const active = this.activeBaseScene(game);
    if (!active || NON_PAUSABLE_KEYS.has(active.scene.key)) return;
    game.scene.start(SceneKeys.Pause);
  }

  private autoLaunchTutorial(game: Phaser.Game) {
    if (this.tutorialLaunchQueued || !PlayerPreferences.instance.value.tutorialEnabled || !TutorialManager.instance.shouldAutoLaunch()) return;
    const campaign = game.scene.getScene(SceneKeys.CampaignMap);
    if (!campaign?.scene.isActive() || game.scene.isActive(SceneKeys.Tutorial)) return;
    this.tutorialLaunchQueued = true;
    window.setTimeout(() => {
      if (campaign.scene.isActive()) {
        campaign.scene.pause();
        game.scene.run(SceneKeys.Tutorial, { returnScene: SceneKeys.CampaignMap, auto: true });
      }
    }, PlayerPreferences.instance.value.reducedMotion ? 0 : 500);
  }

  private showContextHint(game: Phaser.Game) {
    if (!PlayerPreferences.instance.value.contextualHints || game.scene.isActive(SceneKeys.Tutorial) || game.scene.isActive(SceneKeys.Settings)) return;
    const scene = this.activeBaseScene(game);
    if (!scene || scene.scene.key === this.lastHintScene || !HINTS[scene.scene.key]) return;
    this.lastHintScene = scene.scene.key;
    const background = scene.add.rectangle(800, 846, 1180, 62, VisualTheme.colors.ink, 0.94)
      .setStrokeStyle(2, VisualTheme.colors.gold, 0.9)
      .setScrollFactor(0)
      .setDepth(9998);
    const text = scene.add.text(800, 846, `Hint: ${HINTS[scene.scene.key]}  •  F1: full guide`, {
      fontFamily: VisualTheme.fonts.body,
      fontSize: `${PlayerPreferences.instance.fontSize(15)}px`,
      color: VisualTheme.text.body,
      align: 'center',
      wordWrap: { width: 1120 },
    }).setOrigin(0.5).setScrollFactor(0).setDepth(9999);
    scene.time.delayedCall(PlayerPreferences.instance.value.reducedMotion ? 7000 : 5200, () => {
      background.destroy();
      text.destroy();
    });
  }

  private pollGlobalGamepad(game: Phaser.Game) {
    const scene = this.activeBaseScene(game);
    const pad = scene?.input.gamepad?.getPad(0);
    if (!pad) return;
    const guide = Boolean(pad.buttons[8]?.pressed);
    const pause = Boolean(pad.buttons[9]?.pressed);
    const pressed = guide || pause;
    if (pressed && !this.gamepadLatch) {
      if (guide) this.openOverlay(game, SceneKeys.Tutorial);
      else this.handleEscape(game);
    }
    this.gamepadLatch = pressed;
  }
}
